/**
 * Custom hook for managing RAG chat streaming via WebSocket
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IPublicClientApplication } from '@azure/msal-browser';
import { RootState } from '../store/store';
import { setIsStreaming } from '../store/chatModeSlice';
import { RagChatService, RagResponseChunk } from '../utils/websocketRagChat.service';
import logger from '../utils/logger';
import { authSelector } from '../features/auth/authSlice';
import { ChatMessage } from '../utils/chat.service';

/**
 * Parse a "References" (or "Sources") section from the end of a RAG response.
 * TODO: This is subject to change once it's updated to be more like the other RAGs.
 *
 * The backend appends a block like:
 *   **References**
 *   1. [Title](https://...)
 *   2. [Title](https://...)
 *
 * This helper extracts those links into a structured sources array AND returns
 * the content with the raw references section removed so it isn't rendered twice.
 */
function extractReferencesSection(content: string): {
  cleanedContent: string;
  sources: Array<{ url: string; title: string }>;
} {
  // Match an optional horizontal rule (---/***/___ ) followed by the references/sources heading.
  // To be safe, includes: ## References, ### References, **References**, **Sources**
  // Everything from the divider (inclusive) to end-of-string because there were stray <hr>
  // or blank lines are left behind above the SourcesList component.
  const sectionPattern = /\n*(?:[-*_]{3,}\n+)?\n*(?:#{1,4}\s*|\*{1,2})(?:References|Sources|Reference List|Source List)\*{0,2}\s*\n+([\s\S]*?)$/i;
  const match = content.match(sectionPattern);

  if (!match) {
    return { cleanedContent: content, sources: [] };
  }

  const sectionText = match[1];
  const matchStart = content.indexOf(match[0]);
  const cleanedContent = content.slice(0, matchStart).trimEnd();

  // Extract all markdown links from the section.
  // Instead of relying on [^\]]+ for the title (which breaks when titles contain "]"),
  // we anchor on "](https://" as the reliable split point between title and URL.
  // This handles:
  //   - Titles containing "[" or "]" characters (e.g. "[Active Pharmaceutic....")
  //   - URLs containing balanced parentheses (e.g. filenames like "(2023 - 2028).pdf")
  logger.log('🌿 [EverydayInsights] Raw references section text:', sectionText);
  const sources: Array<{ url: string; title: string }> = [];

  // Match: [...anything...](https://...url...)
  // Title: non-backtracking pattern — either non-bracket chars OR complete [...] pairs.
  //        Using (?:[^\]\[]|\[[^\]]*\])* avoids exponential backtracking by ensuring
  //        the two alternatives ([^\]\[] and \[[^\]]*\]) never compete for the same chars.
  // URL:   non-backtracking pattern — non-paren chars, then optionally complete (...) pairs.
  //        Using [^()]*(?:\([^()]*\)[^()]*)* avoids the exponential backtracking caused
  //        by the previous (?:[^()]+|\([^()]*\))+ where both branches could match the same chars.
  const linkPattern = /\[((?:[^[\]]|\[[^\]]*\])*)\]\((https?:\/\/[^()]*(?:\([^()]*\)[^()]*)*)\)/g;
  let linkMatch: RegExpExecArray | null;
  while ((linkMatch = linkPattern.exec(sectionText)) !== null) {
    const title = linkMatch[1].trim();
    const url = linkMatch[2].trim();
    logger.log('🌿 [EverydayInsights] Source —', 'title:', title, '| url:', url);
    sources.push({ title, url });
  }
  logger.log('🌿 [EverydayInsights] Total sources extracted:', sources.length);

  return { cleanedContent, sources };
}

export interface UseRagChatStreamOptions {
  msalInstance: IPublicClientApplication;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

export interface RagChatStreamState {
  isStreaming: boolean;
  error: Error | null;
  currentResponse: string;
  chatHistory: ChatMessage[];
  isConnected: boolean;
}

export const useRagChatStream = ({ msalInstance, onError, onComplete }: UseRagChatStreamOptions) => {
  const sessionId = useSelector((state: RootState) => state.chat.sessionId);
  const auth = useSelector(authSelector);
  const chatModeState = useSelector((state: RootState) => state.chatMode);
  const dispatch = useDispatch();
  const isRagMode = chatModeState.mode === '3M Knowledge';
  const ragServiceRef = useRef<RagChatService | null>(null);
  const currentResponseRef = useRef<string>('');
  const currentMessageIdRef = useRef<string | null>(null);
  const streamTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastChunkRef = useRef<RagResponseChunk | null>(null);
  const allChunksRef = useRef<RagResponseChunk[]>([]);
  const lastChunkContentRef = useRef<string>(''); // Track last chunk content to detect duplicates
  const chunkSequenceRef = useRef<number>(0); // Track chunk sequence for debugging
  const lastUserMessageRef = useRef<string>(''); // Track last user message for retry
  const isStoppedRef = useRef<boolean>(false); // Set true by stopStream to drop late-arriving chunks
  const STREAM_TIMEOUT = 30000; // 30 seconds without any message = stuck

  const [state, setState] = useState<RagChatStreamState>({
    isStreaming: false,
    error: null,
    currentResponse: '',
    chatHistory: [],
    isConnected: false,
  });

  // Sync local isStreaming state to Redux so Header guards are always current
  useEffect(() => {
    dispatch(setIsStreaming(state.isStreaming));
  }, [state.isStreaming, dispatch]);

  // Initialize RAG service
  const getRagService = useCallback(() => {
    // Replace a silently-disconnected service (callbacks were cleared by stopStream)
    // so the next message gets a fresh instance with all callbacks registered.
    if (ragServiceRef.current?.isSilentlyStopped) {
      ragServiceRef.current = null;
    }
    if (!ragServiceRef.current && sessionId && msalInstance) {
      ragServiceRef.current = new RagChatService(sessionId, msalInstance);
      
      // Set up callbacks
      ragServiceRef.current.onMessage((chunk: RagResponseChunk) => {
        // Drop any chunks that arrive after the user pressed Stop
        if (isStoppedRef.current) {
          logger.log('[RAG] Dropping late chunk after stop:', chunk.type);
          return;
        }

        // ===== ENTRY POINT LOGGING - BEFORE ANY PROCESSING =====
        chunkSequenceRef.current++
        logger.log(`🟢 CHUNK #${chunkSequenceRef.current} RECEIVED IN CALLBACK (BEFORE ANY PROCESSING):`, JSON.stringify(chunk));
        logger.log(`🟢 Chunk type: ${chunk.type}, content: ${chunk.content?.substring(0, 100)}`);
        logger.log(`🟢 Previous chunk content: ${lastChunkContentRef.current.substring(0, 100)}`);
        logger.log(`🟢 Are they equal? ${chunk.content === lastChunkContentRef.current}`);
        logger.log('🟢 ==================================================');
        
        // Track this chunk for debugging
        lastChunkRef.current = chunk;
        allChunksRef.current.push(chunk);
        
        // Reset timeout on any message
        if (streamTimeoutRef.current) {
          clearTimeout(streamTimeoutRef.current);
        }
        
        // Set new timeout
        streamTimeoutRef.current = setTimeout(() => {
          logger.error('=== RAG STREAM TIMEOUT DIAGNOSTICS ===');
          logger.error('Stream timeout - no message received for 30 seconds');
          logger.error('Current accumulated response length:', currentResponseRef.current.length);
          logger.error('Current response (last 200 chars):', currentResponseRef.current.slice(-200));
          logger.error('Current response (first 200 chars):', currentResponseRef.current.slice(0, 200));
          logger.error('Last chunk received:', lastChunkRef.current);
          logger.error('Total chunks received:', allChunksRef.current.length);
          logger.error('All chunk types:', allChunksRef.current.map(c => c.type).join(', '));
          
          // Calculate chunk statistics
          const contentChunks = allChunksRef.current.filter(c => c.type === 'chat_response_chunk');
          const totalContentLength = contentChunks.reduce((sum, c) => sum + (c.content?.length || 0), 0);
          const avgChunkSize = contentChunks.length > 0 ? totalContentLength / contentChunks.length : 0;
          
          logger.error('Content chunk statistics:', {
            totalContentChunks: contentChunks.length,
            totalCharacters: totalContentLength,
            averageChunkSize: avgChunkSize.toFixed(2),
            minChunkSize: Math.min(...contentChunks.map(c => c.content?.length || 0)),
            maxChunkSize: Math.max(...contentChunks.map(c => c.content?.length || 0))
          });
          
          logger.error('Last 5 chunks:', allChunksRef.current.slice(-5));
          logger.error('Last 10 chunk sizes:', contentChunks.slice(-10).map(c => c.content?.length));
          logger.error('Ever received chat_response_end?', allChunksRef.current.some(c => c.type === 'chat_response_end'));
          
          // Check for specific cutoff patterns
          const lastContent = contentChunks[contentChunks.length - 1]?.content || '';
          logger.error('Last chunk content:', lastContent);
          logger.error('Last chunk ends with period?', lastContent.endsWith('.'));
          logger.error('Last chunk ends with newline?', lastContent.endsWith('\n'));
          logger.error('Response ends mid-word?', !/[\s.,!?;:]$/.test(currentResponseRef.current));
          
          logger.error('=====================================');
          
          if (currentResponseRef.current) {
            // Save what we have so far with a warning about incomplete response
            const warningMessage = '\n\n⚠️ *Note: This response may be incomplete due to a backend timeout. The service stopped sending data before completing the response.*';
            const { cleanedContent, sources } = extractReferencesSection(currentResponseRef.current);
            const assistantMessage: ChatMessage = {
              id: `${Date.now()}-${Math.random()}`,
              role: 'assistant',
              content: cleanedContent + warningMessage,
              ...(sources.length > 0 ? { sources } : {}),
            };
            
            setState(prev => ({
              ...prev,
              isStreaming: false,
              currentResponse: '',
              chatHistory: [...prev.chatHistory, assistantMessage],
            }));
            
            currentResponseRef.current = '';
            currentMessageIdRef.current = null;
          } else {
            setState(prev => ({
              ...prev,
              isStreaming: false,
              currentResponse: '',
            }));
          }
        }, STREAM_TIMEOUT);
        
        if (chunk.type === 'chat_response_start') {
          logger.log('RAG response started:', chunk.message_id);
          currentMessageIdRef.current = chunk.message_id || null;
          currentResponseRef.current = '';
          lastChunkRef.current = null;
          lastChunkContentRef.current = '';
          chunkSequenceRef.current = 0;
          allChunksRef.current = [chunk];
          
          setState(prev => ({
            ...prev,
            isStreaming: true,
            currentResponse: '',
          }));
        } else if (chunk.type === 'chat_response_chunk' && chunk.content) {
          // Detect and skip duplicate chunks (backend bug workaround)
          // TEMPORARILY DISABLED FOR TESTING
          // if (chunk.content === lastChunkContentRef.current) {
          //   logger.warn('Duplicate chunk detected, skipping:', chunk.content.substring(0, 50));
          //   return; // Skip this duplicate chunk
          // }
          
          lastChunkContentRef.current = chunk.content;
          logger.log(`RAG chunk received (length: ${chunk.content.length}):`, chunk.content.substring(0, 50));
          currentResponseRef.current += chunk.content;

          setState(prev => ({
            ...prev,
            currentResponse: currentResponseRef.current,
          }));
        } else if (chunk.type === 'chat_thinking') {
          // Log thinking steps but don't add to response
          logger.log(`RAG thinking: ${chunk.content} (step: ${chunk.step})`);
        } else if (chunk.type === 'chat_response_end') {
          logger.log('RAG response completed');
          
          // Clear timeout
          if (streamTimeoutRef.current) {
            clearTimeout(streamTimeoutRef.current);
            streamTimeoutRef.current = null;
          }

          // Race-condition guard: the backend occasionally sends the final piece of
          // content inside the chat_response_end event itself.  Append it so nothing
          // is lost before we freeze the message.
          if (chunk.content) {
            currentResponseRef.current += chunk.content;
          }
          
          // Only add message if we have content
          logger.log('🌿 [EverydayInsights] chat_response_end handler reached. currentResponseRef length:', currentResponseRef.current?.length ?? 0);
          if (currentResponseRef.current) {
            // Extract any References/Sources section so it renders via SourcesList
            // instead of as raw markdown text.
            const { cleanedContent, sources } = extractReferencesSection(currentResponseRef.current);

            logger.log('🌿 [EverydayInsights] Calling setState to finalize message. cleanedContent length:', cleanedContent.length, '| sources:', sources.length);
            const assistantMessage: ChatMessage = {
              id: `${Date.now()}-${Math.random()}`,
              role: 'assistant',
              content: cleanedContent,
              ...(sources.length > 0 ? { sources } : {}),
            };
            
            try {
              setState(prev => ({
                ...prev,
                isStreaming: false,
                currentResponse: '',
                chatHistory: [...prev.chatHistory, assistantMessage],
              }));
              logger.log('🌿 [EverydayInsights] setState called successfully — isStreaming set to false');
            } catch (err) {
              logger.error('🌿 [EverydayInsights] setState THREW an error:', err);
            }
          } else {
            // No content to add, just update streaming state
            logger.log('🌿 [EverydayInsights] No content in currentResponseRef — setting isStreaming false (no message added)');
            setState(prev => ({
              ...prev,
              isStreaming: false,
              currentResponse: '',
            }));
          }
          
          currentResponseRef.current = '';
          currentMessageIdRef.current = null;
          lastChunkRef.current = null;
          allChunksRef.current = [];
          
          if (onComplete) {
            onComplete();
          }
        } else if (chunk.type === 'chat_error') {
          const error = new Error(chunk.error || 'Unknown RAG error');
          logger.error('RAG error:', error);
          
          setState(prev => ({
            ...prev,
            isStreaming: false,
            error,
            currentResponse: '',
          }));
          
          if (onError) {
            onError(error);
          }
        }
      });

      ragServiceRef.current.onError((error: Error) => {
        logger.error('RAG service error:', error);
        setState(prev => ({
          ...prev,
          error,
          isStreaming: false,
        }));
        
        if (onError) {
          onError(error);
        }
      });

      ragServiceRef.current.onConnect(() => {
        logger.log('RAG service connected');
        setState(prev => ({
          ...prev,
          isConnected: true,
          error: null,
        }));
      });

      ragServiceRef.current.onDisconnect(() => {
        logger.log('RAG service disconnected');
        setState(prev => ({
          ...prev,
          isConnected: false,
        }));
      });

      // Do NOT auto-connect here. The service's sendMessage handles
      // connecting on demand, so we never open a socket until the user
      // actually sends a message.
    }
    
    return ragServiceRef.current;
  }, [sessionId, msalInstance, onError, onComplete]);

  // Send a message
  const sendMessage = useCallback(async (message: string) => {
    // Clear stop flag so a new message can stream normally
    isStoppedRef.current = false;

    if (state.isStreaming) {
      logger.warn('Already streaming, ignoring new message');
      return;
    }

    // Store message for retry
    lastUserMessageRef.current = message;

    // Use username as fallback if email not available
    const userIdentifier = auth.profile?.mail || auth.profile?.name || 'unknown-user';

    // Add user message to history and start streaming immediately
    const userMessage: ChatMessage = {
      id: `${Date.now()}-${Math.random()}`,
      role: 'user',
      content: message,
    };

    setState(prev => ({
      ...prev,
      chatHistory: [...prev.chatHistory, userMessage],
      isStreaming: true,
      currentResponse: '',
      error: null,
    }));

    // Get or create RAG service
    const ragService = getRagService();
    if (!ragService) {
      const error = new Error('Failed to initialize RAG service');
      logger.error(error);
      if (onError) {
        onError(error);
      }
      return;
    }

    try {
      await ragService.sendMessage(message, userIdentifier);
    } catch (error) {
      // Don't show error for queued messages during initial connection
      // The message will be sent once connection is established
      logger.log('Message queued for sending once connected');
    }
  }, [state.isStreaming, auth.profile?.mail, auth.profile?.name, getRagService, onError]);

  // Clear chat history
  const clearChat = useCallback(() => {
    // Close WebSocket when clearing chat to start fresh conversation
    if (ragServiceRef.current) {
      logger.log('Closing WebSocket connection when clearing chat');
      ragServiceRef.current.disconnect();
      ragServiceRef.current = null;
    }
    
    setState(prev => ({
      ...prev,
      chatHistory: [],
      currentResponse: '',
      error: null,
      isStreaming: false,
    }));
    currentResponseRef.current = '';
  }, []);

  // Stop the current stream
  const stopStream = useCallback(() => {
    logger.log('Stopping RAG stream');

    // Flag FIRST so any already-queued onMessage callbacks are dropped immediately
    isStoppedRef.current = true;
    
    // Clear timeout
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
      streamTimeoutRef.current = null;
    }
    
    // Always commit a stopped message — even if no content arrived yet
    const { cleanedContent, sources } = extractReferencesSection(currentResponseRef.current);
    const stoppedContent = cleanedContent
      ? cleanedContent + '\n\n[Response stopped by user.]'
      : '[Response stopped by user.]';
    const assistantMessage: ChatMessage = {
      id: `${Date.now()}-${Math.random()}`,
      role: 'assistant',
      content: stoppedContent,
      isStopped: true,
      ...(sources.length > 0 ? { sources } : {}),
    };

    setState(prev => ({
      ...prev,
      isStreaming: false,
      currentResponse: '',
      chatHistory: [...prev.chatHistory, assistantMessage],
    }));
    
    // Close WebSocket without triggering the service's reconnect logic.
    // Do NOT null the ref — keeping it prevents the eagerly-initialize effect
    // from immediately creating a new service and re-connecting to the backend
    // (which would receive the still-streaming response for the stopped query).
    if (ragServiceRef.current) {
      ragServiceRef.current.silentDisconnect();
      // ragServiceRef.current stays set intentionally
    }
    
    // Reset refs
    currentResponseRef.current = '';
    currentMessageIdRef.current = null;
  }, []);

  // Retry last message
  const retryLastMessage = useCallback(async () => {
    if (!lastUserMessageRef.current) {
      logger.warn('No message to retry');
      return;
    }

    logger.log('Retrying last message:', lastUserMessageRef.current);
    
    // Remove last assistant message if it exists (the failed/incomplete response)
    setState(prev => {
      const lastMessage = prev.chatHistory[prev.chatHistory.length - 1];
      if (lastMessage?.role === 'assistant') {
        return {
          ...prev,
          chatHistory: prev.chatHistory.slice(0, -1),
        };
      }
      return prev;
    });

    // Resend the last user message
    await sendMessage(lastUserMessageRef.current);
  }, [sendMessage]);

  // Cleanup on unmount or when switching away from RAG mode
  useEffect(() => {
    return () => {
      if (ragServiceRef.current) {
        logger.log('Cleaning up RAG service connection');
        ragServiceRef.current.disconnect();
        ragServiceRef.current = null;
      }
    };
  }, []);

  // Disconnect when switching away from RAG mode
  useEffect(() => {
    if (!isRagMode && ragServiceRef.current) {
      logger.log('Not in RAG mode, disconnecting WebSocket');
      ragServiceRef.current.disconnect();
      ragServiceRef.current = null;
    }
  }, [isRagMode]);

  // NOTE: Intentionally NOT eagerly connecting on mount.
  // Creating a connection before the user sends a message means the backend
  // can route a previous still-streaming response to the new socket.
  // The service is created lazily inside sendMessage instead.

  return {
    ...state,
    sendMessage,
    clearChat,
    stopStream,
    retryLastMessage,
  };
};
